import { ChangeEvent, FC } from 'react';
import { Controller, FieldError, get, useFormContext } from 'react-hook-form';

import { IconButton, InputAdornment } from '@mui/material';
import { ClearIcon } from '@mui/x-date-pickers-pro';

import { renderRequiredLabel } from '@/components/FormElements/RequiredFieldLabel';

import { Input, InputProps } from '../Input';
import { UnitAdornment } from '../Input/UnitAdornment';
import { FormElementProps } from '../types';

export interface FormInputFieldProps
  extends Omit<InputProps, 'name' | 'onChange'>,
    FormElementProps {
  handleChange?: (name: string, value: string | number) => void;
  transformValue?: (value: string) => string;
  clearable?: boolean;
  suffix?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  'data-testid'?: string;
}

export const FormInputField: FC<FormInputFieldProps> = (props) => {
  const {
    name,
    rules,
    defaultValue,
    handleChange,
    transformValue,
    inputRef,
    clearable,
    suffix,
    'data-testid': dataTestId,
    label,
    endAdornment,
    ...rest
  } = props;

  const {
    control,
    formState: { errors },
  } = useFormContext();
  const error = get(errors, name) as FieldError;
  const isRequired = !!rules?.required;

  return (
    <Controller
      control={control}
      defaultValue={defaultValue || ''}
      name={name}
      render={({ field }: any) => (
        <Input
          {...rest}
          {...field}
          {...(inputRef ? { ref: inputRef } : {})}
          label={renderRequiredLabel(label, isRequired)}
          inputProps={{
            ...(dataTestId ? { 'data-testid': dataTestId } : {}),
          }}
          error={error?.message}
          name={name}
          endAdornment={
            clearable && field.value ? (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    field.onChange('');
                    if (handleChange) handleChange(name, '');
                  }}
                  size="small"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ) : suffix ? (
              <UnitAdornment label={suffix} />
            ) : (
              endAdornment || null
            )
          }
          onChange={(
            event: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
          ) => {
            const nextValue = transformValue
              ? transformValue(event.target.value)
              : event.target.value;
            if (handleChange != null)
              handleChange(event.target.name, nextValue);
            field.onChange(nextValue);
          }}
        />
      )}
      rules={rules}
    />
  );
};
